import { useState } from 'react';
import { Button, Input, Segmented, Space, Table, Tag, Tooltip, message } from 'antd';
import {
  CheckCircleTwoTone,
  CheckOutlined,
  DownloadOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  activitiesApi,
  type ActivityListItem,
  type ActivityListQuery,
  type ActivityQuickFilter,
} from '../../../api/endpoints/activities';
import { downloadBlob } from '../../../api/downloadBlob';
import { OwnerType } from '@never2busy/shared';
import { PageHeader } from '../../../components/common/PageHeader';
import { KeywordFilter } from '../../../components/common/KeywordFilter';

type Filter = 'all' | ActivityQuickFilter;

const FILTER_OPTIONS: { label: string; value: Filter }[] = [
  { label: 'Todas', value: 'all' },
  { label: 'Mías', value: 'mine' },
  { label: 'Hoy', value: 'today' },
  { label: 'Vencidas', value: 'overdue' },
  { label: 'Esta semana', value: 'week' },
];

export function ActivitiesListPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [keywordIds, setKeywordIds] = useState<number[]>([]);

  const keywordIdsCsv = keywordIds.length > 0 ? keywordIds.join(',') : undefined;

  const queryParams: ActivityListQuery = {
    page,
    pageSize,
    search: search || undefined,
    sortDir: 'desc',
    quick: filter === 'all' ? undefined : filter,
    keywordIds: keywordIdsCsv,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['activities', queryParams],
    queryFn: () => activitiesApi.list(queryParams),
  });

  const closeMutation = useMutation({
    mutationFn: ({ id, closed }: { id: number; closed: boolean }) =>
      activitiesApi.setClosed(id, closed),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => message.error('No se pudo actualizar la actividad'),
  });

  const handleExport = (): void => {
    downloadBlob('/activities/export.csv', 'actividades.csv', {
      search: search || undefined,
      quick: filter === 'all' ? undefined : filter,
      keywordIds: keywordIdsCsv,
    }).catch(() => message.error('No se pudo exportar'));
  };

  const columns: ColumnsType<ActivityListItem> = [
    {
      title: 'Tipo',
      dataIndex: 'type',
      width: 100,
      render: (v?: string | null) => (v ? <Tag>{v}</Tag> : '—'),
    },
    {
      title: 'Resumen',
      dataIndex: 'notes',
      render: (v: string, row) => (
        <Link to={`/actividades/${row.id}`}>
          {v?.slice(0, 80) ?? ''}
          {v && v.length > 80 ? '…' : ''}
        </Link>
      ),
    },
    {
      title: 'Cuenta',
      render: (_, row) =>
        row.account ? (
          <Link to={`/cuentas/${row.account.id}`}>{row.account.name}</Link>
        ) : (
          row.accountNameSnapshot ?? '—'
        ),
    },
    {
      title: 'Asignada a',
      render: (_, row) =>
        row.assignedTo ? `${row.assignedTo.firstName} ${row.assignedTo.lastName}` : '—',
    },
    {
      title: 'Inicio',
      dataIndex: 'startDate',
      render: (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Estado',
      width: 110,
      align: 'center',
      render: (_, row) =>
        row.closed ? (
          <Tooltip title="Reabrir actividad">
            <Button
              type="text"
              size="small"
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              onClick={() => closeMutation.mutate({ id: row.id, closed: false })}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Marcar como cerrada">
            <Button
              type="default"
              size="small"
              icon={<CheckOutlined />}
              loading={closeMutation.isPending && closeMutation.variables?.id === row.id}
              onClick={() => closeMutation.mutate({ id: row.id, closed: true })}
            >
              Cerrar
            </Button>
          </Tooltip>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Actividades"
        crumbs={[{ title: 'Inicio', to: '/' }, { title: 'Actividades' }]}
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Exportar CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/actividades/nueva')}
            >
              Nueva actividad
            </Button>
          </Space>
        }
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={FILTER_OPTIONS}
          value={filter}
          onChange={(v) => {
            setFilter(v as Filter);
            setPage(1);
          }}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar en notas…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 280 }}
        />
        <KeywordFilter
          ownerType={OwnerType.ACTIVITY}
          value={keywordIds}
          onChange={(ids) => {
            setKeywordIds(ids);
            setPage(1);
          }}
        />
      </Space>

      <Table<ActivityListItem>
        scroll={{ x: 'max-content' }}
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={{
          current: page,
          pageSize,
          total: data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${total} actividades`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </>
  );
}
